import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdSwitch,
  CmdTextarea,
} from '@commander-services/gui-components';
import { useSetRecoilState } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import { TABLE_SUPPLIER } from '../../../Services/TableService';
import PermissionsService, { PERMISSION_SUPPLIER } from '../../../Services/PermissionsService';
import { IOption } from '../../Forms/CmdField';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { setLoaderAtom } from '../../../store/recoil/AppState';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';
import { getRequest, postRequest, putRequest } from '../../../Services/HttpService';

interface ModalFormProps {
  isEdited: boolean;
  closeCallback: () => void;
  selectedId: number | null;
  updateTable?: (() => void) | undefined;
  customerIds?: number | null;
}
type TSupplierFormKeys =
  | 'customerId'
  | 'name'
  | 'active'
  | 'address'
  | 'email'
  | 'phone'
  | 'type'
  | 'note';

type SupplierFormValues = {
  customerId: string;
  name: string;
  active: number;
  address: string;
  email: string;
  phone: string;
  type: string;
  note: string;
};

export default function SuppliersForm(props: ModalFormProps) {
  usePageWithForm();
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<SupplierFormValues>({
    mode: 'onChange',
    defaultValues: {
      customerId: '',
      name: '',
      active: 1,
      address: '',
      email: '',
      phone: '',
      type: '',
      note: '',
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const tableName = TABLE_SUPPLIER;
  const id = props.selectedId;
  const isEdit = props.isEdited;

  const onSubmit = async (values: SupplierFormValues) => {
    const processedValues = {
      ...values,
      customerId: Number(values.customerId),
    };

    if (props.isEdited && id) {
      const url = `/v1/${tableName}/${id}/edit`;
      setLoader(true);

      const response = await putRequest(url, processedValues);

      if (response && props.updateTable) {
        trackEvent(['Suppliers form', 'submit', 'Update']);
        props.updateTable();
        props.closeCallback();
      }

      setLoader(false);
    } else {
      const url = `/v1/${tableName}/add`;
      setLoader(true);

      const response = await postRequest(url, processedValues);

      if (response && props.updateTable) {
        trackEvent(['Suppliers form', 'submit', 'Create']);
        props.updateTable();
        props.closeCallback();
      }

      setLoader(false);
    }
  };

  const getValidation = async () => {
    const url = `/v1/${tableName}/validation`;
    setLoader(true);

    const response = await getRequest<IValidationApiResponse>(url);

    if (response) {
      setValidation(response as IValidationApiResponse);
    }

    setLoader(false);
  };

  const getData = async () => {
    const url = `/v1/${tableName}/${id}${
      props.customerIds ? `?customerId=${props.customerIds}` : ''
    }`;
    setLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if (response && response.status === 200) {
      const data = response?.data?.supplier;

      Object.keys(getValues()).forEach((fieldName) => {
        if (fieldName === 'active') {
          setValue(fieldName as TSupplierFormKeys, data[fieldName] ? 1 : 0);
        } else {
          setValue(fieldName as TSupplierFormKeys, data[fieldName]);
        }
      });
    }

    setLoader(false);
  };

  const getOptionsData = async (name: string) => {
    const url = `/v1/${tableName}/form/${name}`;

    setLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if (response && response.status === 200) {
      const options = response.data;

      if (name === 'customerId' && !props.selectedId) {
        const filterOptionsByPermission = response.data.filter((item: IOption) =>
          PermissionsService.hasAccess(PERMISSION_SUPPLIER, ['create'], Number(item.value))
        );

        return filterOptionsByPermission;
      }

      return options;
    }

    setLoader(false);
    return false;
  };

  const modalButtonsReadOnly: CmdModalFooterButton[] = [
    {
      id: 'supplier-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'suppliersForm.close' }),
      closeCallback: () => {
        trackEvent(['Suppliers form', 'click', 'Close form']);
        props.closeCallback();
      },
    },
  ];

  let isAllowed = true;
  if (isEdit && props.selectedId && props.customerIds) {
    isAllowed = PermissionsService.hasAccess(TABLE_SUPPLIER, ['update'], props.customerIds);
  }

  const results = useQueries({
    queries: [
      {
        queryKey: ['supplierFormData'],
        queryFn: () => getData(),
        enabled: !!props.selectedId,
      },
      {
        queryKey: ['supplierOptionsCustomers'],
        queryFn: () => getOptionsData('customerId'),
      },
      {
        queryKey: ['supplierOptionsTypes'],
        queryFn: () => getOptionsData('type'),
      },
      {
        queryKey: ['supplierFormValidation'],
        queryFn: () => getValidation(),
        enabled: isAllowed,
      },
    ],
  });

  const { isLoading: isLoadingSupplierData } = results[0];
  const { data: customersOptions, isLoading: isLoadingSupplierCustomers } = results[1];
  const { data: typesOptions, isLoading: isLoadingSupplierOptionsTypes } = results[2];
  const { isLoading: isLoadingSupplierValidation } = results[3];

  let customerIdName: IOption | null = null;
  let typeLabelName: IOption | null = null;

  if (customersOptions) {
    customerIdName = customersOptions.filter(
      (items: IOption) => items.value === watch('customerId')
    )[0];
  }

  if (typesOptions) {
    typeLabelName = typesOptions.filter((items: IOption) => items.value === watch('type'))[0];
  }

  const modalButtons: CmdModalFooterButton[] = [
    {
      id: 'supplier-form-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'suppliersForm.close' }),
      closeCallback: () => {
        trackEvent(['Suppliers form', 'click', 'Close form']);
        props.closeCallback();
      },
    },
    {
      id: 'supplier-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'suppliersForm.save' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  React.useEffect(() => {
    if (!isEdit && customersOptions && customersOptions.length === 1) {
      setValue('customerId', customersOptions[0]?.value);
    }
  }, [customersOptions, isEdit, setValue]);

  const isLoading =
    isLoadingSupplierData ||
    isLoadingSupplierCustomers ||
    isLoadingSupplierOptionsTypes ||
    isLoadingSupplierValidation;

  return (
    <CmdModal
      id="supplier-form"
      title={isEdit ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
      footerButtons={isAllowed ? modalButtons : modalButtonsReadOnly}
      loader={isLoading}
    >
      {isAllowed ? (
        <>
          {(isEdit && customerIdName) || (customersOptions && customersOptions.length === 1) ? (
            <CmdLabel
              id="customerId"
              title={f({ id: 'suppliersForm.customer' })}
              value={customerIdName?.item || customersOptions[0]?.item}
              areLabels={true}
            />
          ) : (
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="customerId"
                  label={f({ id: 'suppliersForm.customer' })}
                  introOption={f({ id: 'suppliersForm.choose' })}
                  required={FormikValidatorService.isFieldRequired(
                    validation ? validation.customerId : []
                  )}
                  options={customersOptions || []}
                  onChangeCallback={(value) => {
                    trackEvent(['Suppliers form', 'change', 'Customer', Number(value)]);
                    setValue('customerId', value);
                    trigger('customerId');
                  }}
                  error={errors.customerId?.message}
                  touched={true}
                />
              )}
            />
          )}

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                label={f({ id: 'suppliersForm.company' })}
                id="name"
                type="text"
                required={FormikValidatorService.isFieldRequired(validation ? validation.name : [])}
                onChangeCallback={(value) => {
                  setValue('name', value);
                  trigger('name');
                }}
                error={errors.name?.message}
                touched={true}
              />
            )}
          />
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <CmdSwitch
                {...field}
                id="active"
                label={f({ id: 'suppliersForm.active' })}
                required={FormikValidatorService.isFieldRequired(
                  validation ? validation.active : []
                )}
                setValue={(value) => {
                  setValue('active', value);
                  if (value === '1') {
                    trackEvent(['Suppliers form', 'change', 'Set to active']);
                  }
                  if (value === '0') {
                    trackEvent(['Suppliers form', 'change', 'Set to inactive']);
                  }
                  trigger('active');
                }}
              />
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="address"
                label={f({ id: 'suppliersForm.address' })}
                type="text"
                onChangeCallback={(value) => {
                  setValue('address', value);
                  trigger('address');
                }}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="email"
                label={f({ id: 'suppliersForm.email' })}
                type="text"
                placeholder={f({ id: 'suppliersForm.email.placeholder' })}
                onChangeCallback={(value) => {
                  setValue('email', value);
                  trigger('email');
                }}
                required={FormikValidatorService.isFieldRequired(
                  validation ? validation.email : []
                )}
                error={errors.email?.message}
                touched={true}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="phone"
                label={f({ id: 'suppliersForm.phone' })}
                type="text"
                placeholder={f({ id: 'suppliersForm.phone.placeholder' })}
                onChangeCallback={(value) => {
                  setValue('phone', value);
                  trigger('phone');
                }}
                required={FormikValidatorService.isFieldRequired(
                  validation ? validation.phone : []
                )}
                error={errors.phone?.message}
                touched={true}
              />
            )}
          />
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                id="type"
                label={f({ id: 'suppliersForm.type' })}
                introOption={f({ id: 'suppliersForm.choose' })}
                options={typesOptions || []}
                required={FormikValidatorService.isFieldRequired(validation ? validation.type : [])}
                onChangeCallback={(value) => {
                  trackEvent(['Suppliers form', 'change', `Type: ${value}`]);
                  setValue('type', value);
                  trigger('type');
                }}
                error={errors.type?.message}
                touched={true}
              />
            )}
          />
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <CmdTextarea
                {...field}
                id="note"
                label={f({ id: 'suppliersForm.note' })}
                setValue={(value) => {
                  setValue('note', value);
                  trigger('note');
                }}
                required={FormikValidatorService.isFieldRequired(
                  validation && validation.note ? validation.note : []
                )}
                error={errors.note?.message}
                touched={true}
              />
            )}
          />
        </>
      ) : (
        customerIdName &&
        typeLabelName && (
          <>
            <CmdLabel
              title={f({ id: 'suppliersForm.customer' })}
              value={customerIdName.item}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'suppliersForm.company' })}
              value={watch('name')}
              areLabels={true}
            />
            <CmdLabel
              id="active"
              title={f({ id: 'suppliersForm.active' })}
              value={
                watch('active')
                  ? f({ id: 'operatingCostTypeForm.active.yes' })
                  : f({ id: 'operatingCostTypeForm.active.no' })
              }
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'suppliersForm.address' })}
              value={watch('address')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'suppliersForm.email' })}
              value={watch('email')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'suppliersForm.phone' })}
              value={watch('phone')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'suppliersForm.type' })}
              value={typeLabelName.item}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'suppliersForm.note' })}
              value={watch('note')}
              areLabels={true}
            />
          </>
        )
      )}
    </CmdModal>
  );
}
